import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { ArrowRight, ShoppingCart, Mail, Truck, CheckCircle2, ShieldCheck, Clock, Hammer, MapPin, Award, ArrowUpRight } from "lucide-react";

const HERO_IMG = "/assets/mastro-ritratto.jpg";
const BA_NAVY = "/assets/prima-dopo-navy.jpg";
const BA_BROWN_BEFORE = "/assets/mocassino-prima.jpg";
const BA_BROWN_AFTER = "/assets/mocassino-dopo.jpg";
const SOLES_OLD = "/assets/suole-prima.jpg";
const SOLES_NEW = "/assets/suole-dopo.jpg";
const MOC3_OLD = "/assets/stivaletto-prima.jpg";
const MOC3_NEW = "/assets/stivaletto-dopo.jpg";
const MASTRO_PORTRAIT = "/assets/mastro-ritratto.jpg";
const MASTRO_HANDS = "/assets/mastro-mani.jpg";
const MASTRO_SEWING = "/assets/mastro-cucitura.jpg";

const BRANDS = [
  "Loro Piana", "Brunello Cucinelli", "Kiton", "Hermès",
  "Louis Vuitton", "Bottega Veneta", "Tod's", "Berluti",
  "Santoni", "Church's", "John Lobb", "Edward Green",
  "Gucci", "Prada", "Saint Laurent", "Ferragamo",
];

const STEPS = [
  { icon: ShoppingCart, title: "Acquisti il servizio", desc: "Scegli l'intervento più adatto alle tue calzature di pregio e procedi al pagamento sicuro." },
  { icon: Mail, title: "Ricevi l'etichetta", desc: "Entro poche ore ti inviamo via email le istruzioni di preparazione e l'etichetta prepagata (SDA o DHL)." },
  { icon: Truck, title: "Spedisci al laboratorio", desc: "Porta il pacco al punto Poste/SDA o al punto DHL, in base all'etichetta ricevuta. Tutto incluso." },
  { icon: CheckCircle2, title: "Ricevi le scarpe restaurate", desc: "In 2–3 settimane le tue calzature tornano a casa come nuove. Garanzia 90 giorni." },
];

const TRUST = [
  { icon: Clock, title: "2–3 settimane", desc: "di lavorazione artigianale" },
  { icon: Hammer, title: "Artigianato 100%", desc: "Italiano, fatto a mano" },
  { icon: ShieldCheck, title: "Spedizione assicurata", desc: "Corriere SDA o DHL tracciato" },
  { icon: Award, title: "Garanzia 90 giorni", desc: "sull'intervento completo" },
];

export default function Home() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* === HERO === */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Mastro Giacomo Palombi al lavoro"
            className="w-full h-full object-cover"
            style={{ objectPosition: "70% center", filter: "saturate(0.92) contrast(0.96)" }}
          />
          {/* Cream wash on the left for text readability — photo breathes on the right */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(95deg, rgba(248, 245, 239, 0.96) 0%, rgba(248, 245, 239, 0.88) 30%, rgba(248, 245, 239, 0.55) 52%, rgba(248, 245, 239, 0.18) 72%, rgba(26, 22, 18, 0.05) 100%)",
            }}
          />
          {/* Soft vignette at bottom so the trust strip stays legible */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F8F5EF]/60 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-16 pt-28 pb-32 md:pt-40 md:pb-44 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7 fade-up">
            <div className="eyebrow mb-8">Atelier d'eccellenza · Marche · Italia</div>
            <h1 className="font-serif text-[3.5rem] sm:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight text-[#1A1612]">
              L'Arte<br />
              del <span className="italic gold-shimmer">Restauro</span>.
            </h1>
            <div className="mt-10 max-w-xl">
              <p className="text-lg text-[#5E564C] leading-[1.75] font-light">
                Servizio artigianale italiano di riparazione e restauro per calzature di lusso.
              </p>
              <p className="mt-3 font-serif italic text-xl text-[#8A6436]">
                Dal laboratorio di Mastro Ciabattino Palombi, alle tue scarpe più preziose.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap gap-4 fade-up stagger-2">
              <Link to="/servizi" className="btn-primary" data-testid="hero-cta-services">
                Scopri i servizi <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
              <a href="#come-funziona" className="btn-secondary" data-testid="hero-cta-how">
                Come funziona
              </a>
            </div>

            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 max-w-3xl fade-up stagger-3">
              {TRUST.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3 pr-4 border-l border-[#DED5C3] pl-4">
                  <div className="text-[#A87D4A] mt-0.5"><Icon size={16} strokeWidth={1.2} /></div>
                  <div>
                    <div className="font-medium text-[#1A1612] text-sm">{title}</div>
                    <div className="text-xs text-[#8A8276] mt-0.5 font-light">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === COME FUNZIONA === */}
      <section id="come-funziona" className="py-32 md:py-48 paper-grain" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="max-w-4xl">
            <div className="eyebrow mb-8">Il processo</div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[1.0] tracking-tight">
              Quattro passi.<br />
              <span className="italic text-[#A87D4A]">Nessuna complicazione.</span>
            </h2>
            <div className="divider-ornate mt-10 max-w-md mx-0">
              <span className="divider-ornate-dot" />
            </div>
            <p className="mt-8 text-[#5E564C] max-w-2xl leading-[1.85] text-lg font-light">
              Dall'acquisto alla riconsegna, tutto è pensato per essere semplice e rassicurante.
              Non devi nemmeno uscire di casa.
            </p>
          </div>

          <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="fade-up" style={{ animationDelay: `${i * 0.1}s` }} data-testid={`step-${i + 1}`}>
                <div className="card-luxury p-8 h-full">
                  <div className="flex items-start justify-between mb-8">
                    <span className="numeral">0{i + 1}</span>
                    <div className="mt-4">
                      <Icon size={22} strokeWidth={1.2} className="text-[#1A1612]" />
                    </div>
                  </div>
                  <h3 className="font-serif text-2xl leading-tight mb-3">{title}</h3>
                  <p className="text-[#5E564C] text-sm leading-[1.75] font-light">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === SERVIZI === */}
      <section className="py-32 md:py-48 paper-deep" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="flex items-end justify-between mb-20 flex-wrap gap-6">
            <div>
              <div className="eyebrow mb-6">I nostri interventi</div>
              <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-2xl">
                Quattro servizi,<br />
                <span className="italic">una sola promessa.</span>
              </h2>
            </div>
            <Link to="/servizi" className="link-underline text-sm text-[#1A1612] uppercase tracking-[0.2em]" data-testid="all-services-link">
              Esplora tutti <ArrowRight size={13} className="inline ml-2" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, idx) => (
              <Link to={`/servizi/${s.slug}`} key={s.id} className="card-luxury overflow-hidden flex flex-col fade-up" style={{ animationDelay: `${idx * 0.08}s` }} data-testid={`service-card-${s.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#EFE9DC] relative">
                  <img
                    src={s.image_url}
                    alt={s.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="badge-luxe">N° 0{idx + 1}</span>
                  </div>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="font-serif text-3xl leading-tight">{s.name}</h3>
                  <p className="mt-4 text-sm text-[#5E564C] flex-1 leading-[1.75] font-light">{s.description}</p>
                  <div className="mt-8 pt-6 border-t border-[#DED5C3] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-[#8A8276] uppercase tracking-widest">
                      <Clock size={12} strokeWidth={1.5} /> {s.duration_days} giorni
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-[0.65rem] text-[#8A8276] uppercase tracking-widest">da</span>
                      <span className="font-serif text-3xl text-[#A87D4A]">{eur(s.price_from)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === BRANDS — calzature di pregio che restauriamo === */}
      <section className="py-32 md:py-40 bg-[#1A1612] text-white" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
            <div className="md:col-span-7">
              <div className="eyebrow mb-6" style={{ color: '#A87D4A' }}>I marchi che restauriamo</div>
              <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight">
                Le grandi maison<br />
                <span className="italic gold-shimmer">nelle nostre mani</span>.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-white/75 leading-[1.85] font-light">
                Sono passate dal nostro laboratorio calzature dei più grandi nomi del lusso
                internazionale. Ogni intervento rispetta i materiali, le cuciture e il
                carattere originale della casa madre.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="brands-grid">
            {BRANDS.map((b) => (
              <div key={b} className="bg-[#1A1612] aspect-[3/2] flex items-center justify-center px-4 transition-all duration-500 hover:bg-[#231B14] group" data-testid={`brand-${b.toLowerCase().replace(/[^a-z]/g, '')}`}>
                <span className="font-serif text-xl md:text-2xl text-white/90 group-hover:text-[#A87D4A] tracking-wide text-center transition-colors duration-500">
                  {b}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-12 gap-8 items-center p-8 md:p-12 border border-[#A87D4A]/30 bg-gradient-to-br from-[#A87D4A]/5 to-transparent" data-testid="brands-cta">
            <div className="md:col-span-8">
              <div className="eyebrow mb-3" style={{ color: '#A87D4A' }}>Il tuo brand non è in lista?</div>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight">Mandaci foto e descrizione,<br /><span className="italic">ti rispondiamo con un preventivo</span>.</h3>
              <p className="mt-4 text-white/70 text-sm font-light max-w-xl leading-relaxed">
                Ogni paio di scarpe di lusso ha la sua storia. Scrivici una mail con qualche foto
                e l'intervento che desideri: valutiamo personalmente ogni richiesta.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a
                href="mailto:info@premiumshoescare.com?subject=Richiesta%20preventivo%20personalizzato"
                className="btn-accent inline-flex"
                data-testid="brands-email-cta"
              >
                <Mail size={14} strokeWidth={1.5} />
                Richiedi preventivo
              </a>
              <div className="mt-4 text-xs text-white/60 tracking-widest uppercase">info@premiumshoescare.com</div>
            </div>
          </div>
        </div>
      </section>

      {/* === PRIMA & DOPO === */}
      <section className="py-32 md:py-48" data-testid="before-after">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="max-w-3xl">
            <div className="eyebrow mb-6">Il risultato</div>
            <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight">
              Parla<br />
              <span className="italic gold-shimmer">da solo</span>.
            </h2>
            <p className="mt-8 text-[#5E564C] leading-[1.85] text-lg font-light max-w-2xl">
              Ogni paio che esce dal laboratorio Palombi porta con sé una storia di cura
              e competenza. Ecco alcuni dei nostri lavori recenti.
            </p>
          </div>

          <div className="mt-20 grid gap-8">
            {/* Caso 01 – navy combined */}
            <div className="card-luxury overflow-hidden fade-up" data-testid="ba-pair-1">
              <div className="grid md:grid-cols-5 items-center">
                <div className="md:col-span-3 aspect-[4/3] md:aspect-auto md:h-[32rem] bg-[#EFE9DC]">
                  <img src={BA_NAVY} alt="Mocassino navy prima e dopo" className="w-full h-full object-cover" />
                </div>
                <div className="md:col-span-2 p-10 md:p-14">
                  <div className="eyebrow mb-5">Caso N° 01</div>
                  <h3 className="font-serif text-4xl leading-[1.05]">Mocassini in<br /><span className="italic">camoscio navy</span></h3>
                  <div className="mt-8 grid grid-cols-2 gap-3">
                    <div className="p-5 bg-[#EFE9DC] border border-[#DED5C3]">
                      <div className="overline text-[0.6rem] mb-2">Prima</div>
                      <div className="text-sm text-[#5E564C] leading-relaxed font-light">Camoscio opaco, suola consumata, aloni diffusi</div>
                    </div>
                    <div className="p-5 bg-[#1A1612] text-white">
                      <div className="overline text-[0.6rem] mb-2" style={{ color: '#A87D4A' }}>Dopo</div>
                      <div className="text-sm text-white/85 leading-relaxed font-light">Colore rigenerato, suola nuova, finitura impeccabile</div>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-[#DED5C3]">
                    <div className="overline text-[0.6rem] mb-1">Intervento</div>
                    <div className="text-sm text-[#1A1612] font-medium">Risuolatura + Trattamento Camoscio</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Caso 02 – Suole detail: the drama of resoling */}
            <div className="card-luxury overflow-hidden fade-up" data-testid="ba-pair-soles">
              <div className="grid md:grid-cols-2">
                <div className="relative bg-[#EFE9DC] aspect-[4/3] md:aspect-auto md:min-h-[28rem]">
                  <div className="absolute top-5 left-5 z-10 bg-[#1A1612] text-white overline text-[0.6rem] px-3 py-1.5">Prima</div>
                  <img src={SOLES_OLD} alt="Suole rovinate" className="w-full h-full object-cover" />
                </div>
                <div className="relative bg-[#F8F5EF] aspect-[4/3] md:aspect-auto md:min-h-[28rem]">
                  <div className="absolute top-5 left-5 z-10 bg-[#A87D4A] text-white overline text-[0.6rem] px-3 py-1.5">Dopo</div>
                  <img src={SOLES_NEW} alt="Suole nuove" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="p-10 md:p-12 border-t border-[#DED5C3] grid md:grid-cols-3 gap-8 items-end">
                <div className="md:col-span-2">
                  <div className="eyebrow mb-3">Caso N° 02 · Il dettaglio</div>
                  <h3 className="font-serif text-4xl leading-tight">Suole <span className="italic">rinate</span>.</h3>
                  <p className="mt-4 text-[#5E564C] leading-[1.85] font-light max-w-xl">
                    La risuolatura completa cambia tutto: comfort, postura, longevità della scarpa.
                    Il battistrada originale ricostruito con materiali identici a quelli della casa madre.
                  </p>
                </div>
                <div>
                  <div className="overline text-[0.6rem] mb-1">Intervento</div>
                  <div className="text-sm text-[#1A1612] font-medium">Risuolatura Completa</div>
                  <div className="mt-3 text-xs text-[#A87D4A] uppercase tracking-widest">€120 · 18 giorni</div>
                </div>
              </div>
            </div>

            {/* Caso 02 – brown split */}
            <div className="fade-up" data-testid="ba-pair-2">
              <div className="mb-8 flex items-end justify-between">
                <div>
                  <div className="eyebrow mb-4">Caso N° 03</div>
                  <h3 className="font-serif text-4xl leading-tight">Mocassino in <span className="italic">cognac</span></h3>
                </div>
                <div className="text-right">
                  <div className="overline text-[0.6rem] mb-1">Intervento</div>
                  <div className="text-sm text-[#1A1612] font-medium">Risuolatura Completa</div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative card-luxury overflow-hidden">
                  <div className="absolute top-5 left-5 z-10 bg-[#1A1612] text-white overline text-[0.6rem] px-3 py-1.5">Prima</div>
                  <div className="aspect-[4/3] bg-[#EFE9DC]">
                    <img src={BA_BROWN_BEFORE} alt="Mocassino cognac prima" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="relative card-luxury overflow-hidden">
                  <div className="absolute top-5 left-5 z-10 bg-[#A87D4A] text-white overline text-[0.6rem] px-3 py-1.5">Dopo</div>
                  <div className="aspect-[4/3] bg-[#EFE9DC]">
                    <img src={BA_BROWN_AFTER} alt="Mocassino cognac restaurato" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>

            {/* Caso 04 – moro full */}
            <div className="fade-up" data-testid="ba-pair-3">
              <div className="mb-8 flex items-end justify-between">
                <div>
                  <div className="eyebrow mb-4">Caso N° 04</div>
                  <h3 className="font-serif text-4xl leading-tight">Mocassino <span className="italic">moro testa di moro</span></h3>
                </div>
                <div className="text-right">
                  <div className="overline text-[0.6rem] mb-1">Intervento</div>
                  <div className="text-sm text-[#1A1612] font-medium">Risuolatura + Camoscio</div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative card-luxury overflow-hidden">
                  <div className="absolute top-5 left-5 z-10 bg-[#1A1612] text-white overline text-[0.6rem] px-3 py-1.5">Prima</div>
                  <div className="aspect-[4/3] bg-[#EFE9DC]">
                    <img src={MOC3_OLD} alt="Mocassino moro prima" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="relative card-luxury overflow-hidden">
                  <div className="absolute top-5 left-5 z-10 bg-[#A87D4A] text-white overline text-[0.6rem] px-3 py-1.5">Dopo</div>
                  <div className="aspect-[4/3] bg-[#EFE9DC]">
                    <img src={MOC3_NEW} alt="Mocassino moro restaurato" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === IL LABORATORIO + IL MASTRO === */}
      <section id="filosofia" className="py-32 md:py-48 bg-[#1A1612] text-white" data-testid="laboratorio-section">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-16">
            <div className="md:col-span-5">
              <div className="eyebrow mb-8" style={{ color: '#A87D4A' }}>Il laboratorio</div>
              <h2 className="font-serif text-5xl md:text-7xl leading-[1.0] tracking-tight text-white">
                Mastro<br />
                <span className="italic gold-shimmer">Ciabattino</span><br />
                Palombi.
              </h2>
              <div className="divider-ornate mt-10 max-w-[180px] mx-0">
                <span className="divider-ornate-dot" />
              </div>
              <p className="mt-8 text-white/80 leading-[1.85] text-lg font-light">
                Nel cuore delle Marche, a Magliano di Tenna, Giacomo Palombi
                porta avanti un mestiere antico con strumenti, pazienza e
                tecniche della tradizione artigianale italiana.
              </p>
              <p className="mt-4 font-serif italic text-xl text-[#A87D4A]">
                «La cura delle mani, il tempo del dettaglio.»
              </p>

              <div className="mt-12 inline-block p-8 bg-white/5 border border-white/10 max-w-md">
                <div className="flex items-start gap-4">
                  <MapPin size={18} strokeWidth={1.3} className="text-[#A87D4A] mt-1" />
                  <div className="text-sm font-light leading-relaxed">
                    <div className="font-medium text-white mb-2 tracking-wide">Mastro Ciabattino Palombi Giacomo</div>
                    <div className="text-white/70">Via Indipendenza 50/A</div>
                    <div className="text-white/70">63832 Magliano di Tenna (FM)</div>
                    <div className="text-white/50 text-xs mt-3 tracking-widest">P.IVA 02308600440</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bento grid with 3 Mastro photos */}
            <div className="md:col-span-7 grid grid-cols-2 grid-rows-[auto_auto] gap-3 md:gap-4" data-testid="mastro-bento">
              <div className="col-span-2 aspect-[16/10] overflow-hidden border border-white/10 relative group" data-testid="mastro-portrait">
                <img src={MASTRO_PORTRAIT} alt="Mastro Giacomo Palombi al lavoro" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1612]/80 via-[#1A1612]/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="overline" style={{ color: '#A87D4A' }}>Giacomo Palombi</div>
                  <div className="font-serif text-2xl text-white italic mt-1">Aerografia su pelle ostrich</div>
                </div>
              </div>
              <div className="aspect-square overflow-hidden border border-white/10 relative group" data-testid="mastro-hands">
                <img src={MASTRO_HANDS} alt="Lavorazione della suola a mano" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1612]/70 to-transparent opacity-90" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="overline text-[0.6rem]" style={{ color: '#A87D4A' }}>Il dettaglio</div>
                  <div className="font-serif text-base text-white italic mt-0.5">Costruzione della suola</div>
                </div>
              </div>
              <div className="aspect-square overflow-hidden border border-white/10 relative group" data-testid="mastro-sewing">
                <img src={MASTRO_SEWING} alt="Cucitura su macchina d'epoca" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1612]/70 to-transparent opacity-90" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="overline text-[0.6rem]" style={{ color: '#A87D4A' }}>Il gesto</div>
                  <div className="font-serif text-base text-white italic mt-0.5">Cucitura a punto sellaio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === CTA FINALE === */}
      <section className="py-32 md:py-40 paper-grain" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="eyebrow mb-8 justify-center" style={{ display: 'inline-flex' }}>Affida le tue scarpe</div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[1.05] tracking-tight">
            Le tue calzature meritano<br />
            <span className="italic gold-shimmer">mani esperte</span>.
          </h2>
          <div className="divider-ornate mt-10 justify-center">
            <span className="divider-ornate-dot" />
          </div>
          <p className="mt-8 text-[#5E564C] leading-[1.85] text-lg font-light max-w-2xl mx-auto">
            Spedizione via SDA o DHL, lavorazione artigianale, riconsegna entro 2–3 settimane.
            Tutto incluso, nessuna sorpresa.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/servizi" className="btn-primary" data-testid="final-cta-services">
              Scopri i servizi <ArrowRight size={14} strokeWidth={1.8} />
            </Link>
            <Link to="/shop" className="btn-secondary" data-testid="final-cta-shop">
              Kit Fai Da Te
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
